import { useRef, useState } from "react";
import { UserPlus, Upload, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDuration, formatErr } from "@/lib/api";

const inputCls = "h-10 px-3 rounded-md bg-[#0B0F17] border border-[#242C3D] text-sm focus:border-indigo-500 focus:outline-none";
const SAMPLE = "email,start_time,end_time,score,completion,sessions\njane@acme.com,2026-06-10 14:00,2026-06-10 14:45,85,100,2\njohn@acme.com,2026-06-10 14:05,2026-06-10 14:30,,60,\n";

const CsvImport = ({ cid, onDone }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/campaigns/${cid}/manual-entries/csv`, fd);
      setRes(data);
      toast.success(`${data.added} added · ${data.updated} updated${data.errors.length ? ` · ${data.errors.length} skipped` : ""}`);
      onDone();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const sample = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([SAMPLE], { type: "text/csv" }));
    a.download = "manual-entries-sample.csv";
    a.click();
  };
  return (
    <div className="mt-5 pt-5 border-t border-[#242C3D]">
      <input ref={ref} type="file" accept=".csv,text/csv" className="hidden" data-testid="manual-csv-input" onChange={(e) => upload(e.target.files[0])} />
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" data-testid="manual-csv-upload-button" disabled={busy} onClick={() => ref.current?.click()}
          className="h-10 px-4 rounded-full border border-[#242C3D] hover:bg-white/5 text-sm flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload CSV
        </button>
        <button type="button" data-testid="manual-csv-sample-button" onClick={sample} className="text-xs text-slate-400 hover:text-white underline flex items-center gap-1"><Download className="w-3 h-3" /> Sample CSV</button>
        <span className="text-xs text-slate-500">Columns: email, start_time, end_time, optional score (0–100), completion (0–100 %) and sessions (1+). Times without a timezone are treated as UTC.</span>
      </div>
      {res?.errors?.length > 0 && (
        <ul data-testid="manual-csv-errors" className="mt-3 max-h-32 overflow-y-auto text-xs text-rose-300 space-y-1">
          {res.errors.map((e) => <li key={e.row}>Row {e.row}{e.email ? ` (${e.email})` : ""}: {e.error}</li>)}
        </ul>
      )}
    </div>
  );
};

export const ManualEntriesPanel = ({ campaign: c, onDone }) => {
  const blank = { email: "", start: "", end: "", score: "", pct: "", sessions: "" };
  const [f, setF] = useState(blank);
  const wp = c.campaign_type === "whitepaper";
  const [busy, setBusy] = useState(false);
  const dur = f.start && f.end ? (new Date(f.end) - new Date(f.start)) / 1000 : 0;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/campaigns/${c.id}/manual-entries`, {
        email: f.email, start_time: new Date(f.start).toISOString(), end_time: new Date(f.end).toISOString(),
        score: f.score === "" ? null : Number(f.score), completion: f.pct === "" ? null : Number(f.pct), sessions: f.sessions === "" ? null : Number(f.sessions),
      });
      toast.success(data.status === "added" ? "Lead and view added" : "View added to existing lead");
      setF(blank);
      onDone();
    } catch (ex) { toast.error(formatErr(ex)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-[#242C3D] bg-[#141A24]/70 p-6 mb-8" data-testid="manual-entries-panel">
      <h3 className="font-display font-semibold mb-1 flex items-center gap-2"><UserPlus className="w-4 h-4 text-indigo-400" /> Add views / leads manually</h3>
      <p className="text-xs text-slate-500 mb-4">For offline or live-event attendees. Each entry adds a visit, the lead (if new) and a {c.campaign_type === "whitepaper" ? "reading" : "viewing"} session counted in reports.</p>
      <form onSubmit={submit} className="grid sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_0.6fr_0.7fr_0.6fr_auto_auto] gap-3 items-end">
        <label className="text-xs text-slate-400 space-y-1.5">Email
          <input data-testid="manual-email-input" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="name@company.com" className={`${inputCls} w-full`} />
        </label>
        <label className="text-xs text-slate-400 space-y-1.5">Start time
          <input data-testid="manual-start-input" type="datetime-local" required value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className={`${inputCls} w-full [color-scheme:dark]`} />
        </label>
        <label className="text-xs text-slate-400 space-y-1.5">End time
          <input data-testid="manual-end-input" type="datetime-local" required value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className={`${inputCls} w-full [color-scheme:dark]`} />
        </label>
        <label className="text-xs text-slate-400 space-y-1.5">Score <span className="text-slate-600">(optional)</span>
          <input data-testid="manual-score-input" type="number" min={0} max={100} step={1} value={f.score} placeholder="Auto" onChange={(e) => setF({ ...f, score: e.target.value })} className={`${inputCls} w-full`} />
        </label>
        <label className="text-xs text-slate-400 space-y-1.5">{wp ? "% read" : "% viewed"} <span className="text-slate-600">(optional)</span>
          <input data-testid="manual-completion-input" type="number" min={0} max={100} step="any" value={f.pct} placeholder="0–100" onChange={(e) => setF({ ...f, pct: e.target.value })} className={`${inputCls} w-full`} />
        </label>
        <label className="text-xs text-slate-400 space-y-1.5">Sessions <span className="text-slate-600">(opt.)</span>
          <input data-testid="manual-sessions-input" type="number" min={1} max={1000} step={1} value={f.sessions} placeholder="1" onChange={(e) => setF({ ...f, sessions: e.target.value })} className={`${inputCls} w-full`} />
        </label>
        <div className="text-xs text-slate-400 pb-3" data-testid="manual-duration">{dur > 0 ? fmtDuration(dur) : "—"}</div>
        <button data-testid="manual-add-button" disabled={busy || dur <= 0 || (f.score !== "" && (f.score < 0 || f.score > 100)) || (f.pct !== "" && (f.pct < 0 || f.pct > 100)) || (f.sessions !== "" && (f.sessions < 1 || f.sessions > 1000))} className="h-10 px-5 rounded-full bg-indigo-500 hover:bg-indigo-400 text-sm font-medium disabled:opacity-50">{busy ? "Adding…" : "Add"}</button>
      </form>
      <CsvImport cid={c.id} onDone={onDone} />
    </div>
  );
};
